<script setup>
import { reactive, computed, watch, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

// submit(details, { done, fail }): the dialog tells the form whether the
// save went through; the form only clears itself on success.
const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  selectedStages: [],
  // Free text for the assistant on this campaign (what the offer includes,
  // until when, what to do on «أبغى أحجز»). Optional.
  assistantNotes: '',
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required },
  // Labels or lifecycle stages: at least one of the two.
  selectedAudience: {
    required: requiredIf(() => !state.selectedStages?.length),
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);
// Lifecycle stages as a second audience axis (owner order 2026-09-21):
// shown only when the company has lifecycle enabled.
const lifecycleReady = useMapGetter('customerLifecycle/isReady');
const lifecycleStages = useMapGetter('customerLifecycle/getActiveStages');
const stageList = computed(() =>
  (lifecycleStages.value || []).map(stage => ({
    value: stage.id,
    label: `${stage.emoji || ''} ${stage.name}`.trim(),
  }))
);
onMounted(() => {
  store.dispatch('customerLifecycle/getSettings').then(settings => {
    if (settings?.available && settings?.enabled)
      store.dispatch('customerLifecycle/getStages');
  });
});

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

// Owner order 2026-09-14 (campaign audit): the old guard read
// `... === false || true`, i.e. always true, so a campaign could be created
// with every template variable blank. The parser exposes its own vuelidate
// state; it is the authority here.
const hasRequiredTemplateParams = computed(() => {
  const parserValidation = templateParserRef.value?.v$;
  if (!parserValidation) return !selectedTemplate.value;
  return !parserValidation.$invalid;
});

const submitError = ref('');
const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

// Live audience count: the same query the send loop runs, so "0" here is
// exactly the campaign that used to complete with nobody.
const audience = ref(null);
const audienceLoading = ref(false);
let audienceRequest = 0;
const loadAudience = async () => {
  const ids = state.selectedAudience || [];
  const stageIds = state.selectedStages || [];
  if (!ids.length && !stageIds.length) {
    audience.value = null;
    return;
  }
  audienceRequest += 1;
  const seq = audienceRequest;
  audienceLoading.value = true;
  try {
    const { data } = await CampaignsAPI.audiencePreview(ids, stageIds);
    if (seq === audienceRequest) audience.value = data.payload;
  } catch (e) {
    if (seq === audienceRequest) audience.value = null;
  } finally {
    if (seq === audienceRequest) audienceLoading.value = false;
  }
};
watch(
  () => [[...state.selectedAudience], [...state.selectedStages]],
  loadAudience,
  { deep: true }
);

const audienceEmpty = computed(
  () => audience.value !== null && audience.value.reachable === 0
);
const audienceMessage = computed(() => {
  if (audienceLoading.value)
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.COUNTING');
  if (!audience.value) return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.HINT');
  if (audience.value.total === 0)
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.EMPTY');
  return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.COUNT', {
    total: audience.value.total,
    reachable: audience.value.reachable,
    withoutPhone: audience.value.without_phone,
    blocked: audience.value.blocked,
    optedOut: audience.value.opted_out,
  });
});
const audienceMessageType = computed(() =>
  formErrors.value.audience || audienceEmpty.value ? 'error' : 'info'
);

// Paste a list of numbers: they become labelled contacts and the label is
// selected as the audience, so "numbers -> campaign" is one box.
const pasteOpen = ref(false);
const pasteNumbers = ref('');
const pasteLabel = ref('');
const pasting = ref(false);
const pasteResult = ref(null);
const pasteAudience = async () => {
  if (pasting.value || !pasteNumbers.value.trim() || !pasteLabel.value.trim())
    return;
  pasting.value = true;
  pasteResult.value = null;
  try {
    const { data } = await CampaignsAPI.pasteAudience(
      pasteNumbers.value,
      pasteLabel.value.trim()
    );
    pasteResult.value = data.payload;
    await store.dispatch('labels/get');
    if (
      data.payload.label_id &&
      !state.selectedAudience.includes(data.payload.label_id)
    ) {
      state.selectedAudience = [
        ...state.selectedAudience,
        data.payload.label_id,
      ];
    }
    pasteNumbers.value = '';
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.ERROR')
    );
  } finally {
    pasting.value = false;
  }
};

const templateCategoryNote = computed(() => {
  const category = selectedTemplate.value?.category;
  if (!category) return '';
  const key = category === 'MARKETING' ? 'MARKETING' : 'UTILITY';
  return t(`CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.CATEGORY_NOTE.${key}`);
});

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, {
    ...initialState,
    selectedAudience: [],
    selectedStages: [],
  });
  audience.value = null;
  submitError.value = '';
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    assistant_notes: (state.assistantNotes || '').trim(),
    audience: [
      ...(state.selectedAudience || []).map(id => ({ id, type: 'Label' })),
      ...(state.selectedStages || []).map(id => ({
        id,
        type: 'LifecycleStage',
      })),
    ],
  };
};

const handleSubmit = async () => {
  submitError.value = '';
  const isFormValid = await v$.value.$validate();
  const parserValidation = templateParserRef.value?.v$;
  const isParserValid = parserValidation
    ? await parserValidation.$validate()
    : true;
  if (!isFormValid || !isParserValid) return;
  if (audienceEmpty.value) return;

  // The dialog closes the form only after the server said yes; on failure the
  // inputs stay so the manager can fix them instead of retyping everything.
  emit('submit', prepareCampaignDetails(), {
    done: () => {
      resetState();
      handleCancel();
    },
    fail: message => {
      submitError.value = message;
    },
  });
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
      <p v-if="templateCategoryNote" class="mt-1 text-xs text-n-amber-11">
        {{ templateCategoryNote }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience || audienceEmpty"
        :message="formErrors.audience || audienceMessage"
        :message-type="audienceMessageType"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <TagMultiSelectComboBox
        v-if="lifecycleReady"
        v-model="state.selectedStages"
        :options="stageList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.PLACEHOLDER')"
        :message="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.HINT')"
        message-type="info"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <button
        type="button"
        class="w-fit text-xs text-n-blue-11 hover:underline"
        @click="pasteOpen = !pasteOpen"
      >
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.TOGGLE') }}
      </button>
      <div
        v-if="pasteOpen"
        class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      >
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.HINT') }}
        </p>
        <textarea
          v-model="pasteNumbers"
          rows="4"
          dir="ltr"
          class="mb-0 text-sm font-mono"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.PLACEHOLDER')"
        />
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            v-model="pasteLabel"
            type="text"
            class="mb-0 text-sm sm:flex-1"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.LABEL_PLACEHOLDER')
            "
          />
          <Button
            type="button"
            size="sm"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.SUBMIT')"
            :is-loading="pasting"
            :disabled="pasting || !pasteNumbers.trim() || !pasteLabel.trim()"
            @click="pasteAudience"
          />
        </div>
        <p v-if="pasteResult" class="mb-0 text-xs text-n-slate-12">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.PASTE.RESULT', {
              created: pasteResult.created,
              updated: pasteResult.updated,
              duplicates: pasteResult.duplicates,
              invalid: pasteResult.invalid.length,
            })
          }}
          <span
            v-if="pasteResult.invalid.length"
            dir="ltr"
            class="text-n-ruby-11"
          >
            {{ pasteResult.invalid.slice(0, 5).join(', ') }}
          </span>
        </p>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <label class="flex flex-col gap-1 text-sm text-n-slate-12">
      {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT_NOTES.LABEL') }}
      <textarea
        v-model="state.assistantNotes"
        rows="3"
        maxlength="2000"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT_NOTES.PLACEHOLDER')
        "
        class="mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 py-1 text-sm"
      />
      <span class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT_NOTES.HINT') }}
      </span>
    </label>

    <p v-if="submitError" class="mb-0 text-sm text-n-ruby-11">
      {{ submitError }}
    </p>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled || audienceEmpty"
      />
    </div>
  </form>
</template>
